import { createContext, useContext } from 'react';
import * as Intercom from '@intercom/messenger-js-sdk';
import { initAmplitude, AmplitudeInterface, initAmplitudeStub } from './amplitude';
import { initFbPixel, trackFbEvent } from '@arthelper/shared/facebook-pixel';

interface Analytics {
  amplitude: AmplitudeInterface;
  intercom: any;
  gtm: any;
  fbPixel: { track: typeof trackFbEvent };
}

export const AnalyticsContext = createContext<Analytics>({
  amplitude: initAmplitudeStub(),
  intercom: {
    boot: () => {},
    shutdown: () => {},
    trackEvent: () => {},
  },
  gtm: {
    push: () => {},
  },
  fbPixel: { track: trackFbEvent },
});

function initializeAmplitude() {
  if (['staging', 'production', 'mattmills'].includes(__STAGE__) && __AK__) {
    return initAmplitude(__AK__);
  }

  return initAmplitudeStub();
}

function stub(method: string) {
  return (...args: any[]) => {
    console.log(`Calling Intercom.${method} with args:`, ...args);
  };
}

function initializeIntercom() {
  if (__IAID__) {
    Intercom.Intercom({
      app_id: __IAID__,
      z_index: 100000,
    });
    return Intercom;
  }
  // Return mock methods for development
  return {
    Intercom: () => {},
    boot: stub('boot'),
    default: () => {},
    getVisitorId: stub('getVisitorId'),
    hide: stub('hide'),
    onHide: stub('onHide'),
    onShow: stub('onShow'),
    onUnreadCountChange: stub('onUnreadCountChange'),
    onUserEmailSupplied: stub('onUserEmailSupplied'),
    show: stub('show'),
    showArticle: stub('showArticle'),
    showConversation: stub('showConversation'),
    showMessages: stub('showMessages'),
    showNewMessage: stub('showNewMessage'),
    showNews: stub('showNews'),
    showSpace: stub('showSpace'),
    showTicket: stub('showTicket'),
    shutdown: stub('shutdown'),
    startChecklist: stub('startChecklist'),
    startSurvey: stub('startSurvey'),
    startTour: stub('startTour'),
    trackEvent: stub('trackEvent'),
    update: stub('update'),
  };
}

function initializeFbPixel() {
  if (['production'].includes(__STAGE__) && __FACEBOOK_PIXEL_ID__) {
    initFbPixel(__FACEBOOK_PIXEL_ID__);
    return { track: trackFbEvent };
  }

  console.log(`Stubbing FB Pixel for stage:${__STAGE__}`);
  return { track: trackFbEvent };
}

function initializeGTM() {
  if (['production'].includes(__STAGE__)) {
    // Initialize GTM
    const GTM_ID = 'GTM-K98WTSTW';
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    document.head.appendChild(script);

    // Add noscript iframe
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.appendChild(noscript);

    return {
      push: (...args: any[]) => (window as any).dataLayer.push(...args),
    };
  }

  console.log(`Stubbing GTM for stage:${__STAGE__}`);
  return {
    push: stub('gtm.push'),
  };
}

const analytics: any = {
  amplitude: initializeAmplitude(),
  intercom: initializeIntercom(),
  gtm: initializeGTM(),
  fbPixel: initializeFbPixel(),
};

export function AnalyticsProvider({ children }: any) {
  return <AnalyticsContext.Provider value={analytics}>{children}</AnalyticsContext.Provider>;
}

export function useAnalytics() {
  return useContext(AnalyticsContext);
}
